import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationService, WhatsAppConfiguration } from '../../services/notification.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-whatsapp-config',
  templateUrl: './whatsapp-config.component.html',
  styleUrls: ['./whatsapp-config.component.css']
})
export class WhatsappConfigComponent implements OnInit {
  configurations: WhatsAppConfiguration[] = [];
  providers: any[] = [];
  loading = false;
  showForm = false;
  editingId: number | null = null;

  configForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private notificationService: NotificationService
  ) {
    this.configForm = this.fb.group({
      provider: ['', [Validators.required]],
      api_url: ['', [Validators.required, Validators.pattern('https?://.+')]],
      api_key: ['', [Validators.required]],
      api_secret: [''],
      phone_number_id: [''],
      from_number: ['', [Validators.required]],
      additional_config: [{}],
      is_active: [false]
    });
  }

  ngOnInit(): void {
    this.loadConfigurations();
    this.loadProviders();
  }

  loadConfigurations(): void {
    this.loading = true;
    this.notificationService.getWhatsAppConfigurations().subscribe({
      next: (response) => {
        this.configurations = response.data || [];
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading configurations:', error);
        this.loading = false;
        Swal.fire('Error', 'No se pudieron cargar las configuraciones de WhatsApp', 'error');
      }
    });
  }

  loadProviders(): void {
    this.notificationService.getWhatsAppProviders().subscribe({
      next: (response) => {
        this.providers = response.data || [];
      },
      error: (error) => {
        console.error('Error loading providers:', error);
      }
    });
  }

  showCreateForm(): void {
    this.showForm = true;
    this.editingId = null;
    this.configForm.reset({
      is_active: false,
      additional_config: {}
    });
  }

  editConfiguration(config: WhatsAppConfiguration): void {
    if (!config.id) {
      Swal.fire('Error', 'No se puede editar una configuración sin ID', 'error');
      return;
    }
    
    this.showForm = true;
    this.editingId = config.id;
    this.configForm.patchValue(config);
  }

  onProviderChange(): void {
    const selectedProvider = this.configForm.get('provider')?.value;
    const provider = this.providers.find(p => p.value === selectedProvider);
    
    if (provider) {
      // Set default API URLs based on provider
      switch (selectedProvider) {
        case 'twilio':
          this.configForm.patchValue({
            api_url: 'https://api.twilio.com/2010-04-01/Accounts/YOUR_ACCOUNT_SID/Messages.json'
          });
          break;
        case 'whatsapp_business_api':
          this.configForm.patchValue({
            api_url: 'https://graph.facebook.com/v18.0'
          });
          break;
        case 'ultramsg':
          this.configForm.patchValue({
            api_url: 'https://api.ultramsg.com/instance_id/messages/chat'
          });
          break;
        case 'd360':
          this.configForm.patchValue({
            api_url: 'https://waba-v2.360dialog.io/messages'
          });
          break;
      }
    }
  }

  getRequiredFields(): string[] {
    const selectedProvider = this.configForm.get('provider')?.value;
    const provider = this.providers.find(p => p.value === selectedProvider);
    return provider ? provider.required_fields : [];
  }

  isFieldRequired(fieldName: string): boolean {
    return this.getRequiredFields().includes(fieldName);
  }

  onSubmit(): void {
    if (this.configForm.valid) {
      const formData = this.configForm.value;
      
      if (this.editingId) {
        this.updateConfiguration(this.editingId, formData);
      } else {
        this.createConfiguration(formData);
      }
    }
  }

  createConfiguration(data: WhatsAppConfiguration): void {
    this.notificationService.createWhatsAppConfiguration(data).subscribe({
      next: (response) => {
        Swal.fire('Éxito', 'Configuración de WhatsApp creada correctamente', 'success');
        this.loadConfigurations();
        this.cancelForm();
      },
      error: (error) => {
        console.error('Error creating configuration:', error);
        Swal.fire('Error', 'No se pudo crear la configuración de WhatsApp', 'error');
      }
    });
  }

  updateConfiguration(id: number, data: WhatsAppConfiguration): void {
    this.notificationService.updateWhatsAppConfiguration(id, data).subscribe({
      next: (response) => {
        Swal.fire('Éxito', 'Configuración de WhatsApp actualizada correctamente', 'success');
        this.loadConfigurations();
        this.cancelForm();
      },
      error: (error) => {
        console.error('Error updating configuration:', error);
        Swal.fire('Error', 'No se pudo actualizar la configuración de WhatsApp', 'error');
      }
    });
  }

  testConfiguration(config: WhatsAppConfiguration): void {
    if (!config.id) {
      Swal.fire('Error', 'No se puede probar una configuración sin ID', 'error');
      return;
    }

    Swal.fire({
      title: 'Probando configuración...',
      text: 'Se enviará un mensaje de prueba',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    this.notificationService.testWhatsAppConfiguration(config.id).subscribe({
      next: (response) => {
        Swal.close();
        if (response.success) {
          Swal.fire('Éxito', response.message, 'success');
        } else {
          Swal.fire('Error', response.message, 'error');
        }
      },
      error: (error) => {
        Swal.close();
        console.error('Error testing configuration:', error);
        Swal.fire('Error', 'No se pudo probar la configuración', 'error');
      }
    });
  }

  sendTestMessage(): void {
    Swal.fire({
      title: 'Enviar mensaje de prueba',
      html: `
        <div class="mb-3">
          <label class="form-label">Número de teléfono:</label>
          <input type="tel" id="phone" class="form-control" placeholder="+573001234567">
        </div>
        <div class="mb-3">
          <label class="form-label">Mensaje:</label>
          <textarea id="message" class="form-control" rows="3" placeholder="Mensaje de prueba"></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Enviar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const phone = (document.getElementById('phone') as HTMLInputElement).value;
        const message = (document.getElementById('message') as HTMLTextAreaElement).value;
        
        if (!phone || !message) {
          Swal.showValidationMessage('Por favor complete todos los campos');
          return false;
        }
        
        return { phone, message };
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const { phone, message } = result.value;
        
        Swal.fire({
          title: 'Enviando mensaje...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        this.notificationService.sendTestWhatsAppMessage(phone, message).subscribe({
          next: (response) => {
            Swal.close();
            if (response.success) {
              Swal.fire('Éxito', 'Mensaje enviado correctamente', 'success');
            } else {
              Swal.fire('Error', 'No se pudo enviar el mensaje', 'error');
            }
          },
          error: (error) => {
            Swal.close();
            console.error('Error sending test message:', error);
            Swal.fire('Error', 'No se pudo enviar el mensaje de prueba', 'error');
          }
        });
      }
    });
  }

  deleteConfiguration(config: WhatsAppConfiguration): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        if (!config.id) {
          Swal.fire('Error', 'No se puede eliminar una configuración sin ID', 'error');
          return;
        }
        
        this.notificationService.deleteWhatsAppConfiguration(config.id).subscribe({
          next: (response) => {
            Swal.fire('Eliminado', 'Configuración eliminada correctamente', 'success');
            this.loadConfigurations();
          },
          error: (error) => {
            console.error('Error deleting configuration:', error);
            Swal.fire('Error', 'No se pudo eliminar la configuración', 'error');
          }
        });
      }
    });
  }

  cancelForm(): void {
    this.showForm = false;
    this.editingId = null;
    this.configForm.reset();
  }

  getProviderName(providerValue: string): string {
    const provider = this.providers.find(p => p.value === providerValue);
    return provider ? provider.label : providerValue;
  }

  getProviderDescription(providerValue: string): string {
    const provider = this.providers.find(p => p.value === providerValue);
    return provider ? provider.description : '';
  }
}
